/**
 * GlobalView — Wildfire Layer Renderer
 *
 * Renders NASA FIRMS active fire detections as glowing,
 * pulsing ember points on the globe.
 *
 * Architecture:
 *   - PointGraphics per fire detection (clamped to ground)
 *   - Animated pulsing glow via CallbackProperty on pixelSize + alpha
 *   - Color mapped from confidence: h → red, n → orange, l → yellow
 *   - Size scaled by FRP (fire radiative power)
 *   - requestAnimationFrame drives continuous rendering
 *   - 10-minute refresh interval (conservative for 5000 txn / 10 min limit)
 *
 * Data: NASA FIRMS VIIRS SNPP NRT CSV (MAP_KEY required)
 */

import * as Cesium from 'cesium';
import { useGlobeStore, type VisualMode } from '../../stores/globeStore';
import { useEntityStore, selectWildfireCount } from '../../stores/entityStore';
import {
  fetchActiveFires,
  confidenceToColor,
  frpToSize,
} from '../firmsClient';
import type { Entity } from '@globalview/shared';

// ─── Config ──────────────────────────────────────────────────────

/** Max fire points to render (FIRMS can return thousands) */
const MAX_FIRES = 2000;

/** Data refresh interval (ms) — 10 minutes */
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;

/** Pulse animation cycle (ms) */
const PULSE_CYCLE_MS = 2000;

/** Minimum pixel size for fire dots */
const MIN_PIXEL_SIZE = 4;

/** Maximum pixel size for fire dots */
const MAX_PIXEL_SIZE = 14;

/** Pulse amplitude (fraction of base size) */
const PULSE_AMPLITUDE = 0.35;

/** Base alpha for fire glow */
const BASE_ALPHA = 0.85;

/** Alpha pulse range */
const ALPHA_PULSE = 0.15;

// ─── Module State ────────────────────────────────────────────────

interface FireLayerState {
  viewer: Cesium.Viewer;
  dataSource: Cesium.CustomDataSource;
  unsubscribeVisibility: (() => void) | null;
  unsubscribeVisualMode: (() => void) | null;
  refreshTimer: ReturnType<typeof setInterval> | null;
  loading: boolean;
  loaded: boolean;
}

let state: FireLayerState | null = null;
let animFrameId: number | null = null;
let animRunning = false;

/** Current visual mode — tracked for FLIR/NVG brightness boost */
let currentVisualMode: VisualMode = 'standard';

// ─── Hash → Phase Offset ─────────────────────────────────────────

function hashToPhase(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return (Math.abs(hash) % 1000) / 1000;
}

// ─── Entity Creation ─────────────────────────────────────────────

function buildEntities(fires: Entity[]): void {
  if (!state) return;

  const { dataSource } = state;
  dataSource.entities.removeAll();

  // Sort by FRP descending → most intense fires rendered first
  const sorted = [...fires]
    .sort((a, b) => {
      const frpA = (a.properties as Record<string, unknown>).frp as number ?? 0;
      const frpB = (b.properties as Record<string, unknown>).frp as number ?? 0;
      return frpB - frpA;
    })
    .slice(0, MAX_FIRES);

  for (const fire of sorted) {
    const props = fire.properties as Record<string, unknown>;
    const frp = (props.frp as number) ?? 0;
    const confidence = (props.confidence as string) ?? 'n';
    const baseColor = Cesium.Color.fromCssColorString(confidenceToColor(confidence));
    const baseSizeMult = frpToSize(frp);
    const phaseOffset = hashToPhase(fire.id);

    // Compute base pixel size from FRP
    const basePixelSize = Math.min(
      Math.max(MIN_PIXEL_SIZE, MIN_PIXEL_SIZE * baseSizeMult),
      MAX_PIXEL_SIZE,
    );

    // Pre-allocate scratch objects (zero GC pressure)
    const scratchColor = baseColor.clone();

    // Hot-white color for FLIR/NVG intensity boost
    const hotColor = new Cesium.Color(1.0, 0.98, 0.9, 1.0);

    dataSource.entities.add({
      id: `fire-${fire.id}`,
      position: Cesium.Cartesian3.fromDegrees(
        fire.position.lon,
        fire.position.lat,
        0,
      ),
      point: {
        pixelSize: new Cesium.CallbackProperty(() => {
          const t = ((Date.now() / PULSE_CYCLE_MS) + phaseOffset) % 1.0;
          const pulse = Math.sin(t * Math.PI * 2) * 0.5 + 0.5; // 0..1
          const isBoosted = currentVisualMode === 'flir' || currentVisualMode === 'nvg';
          const sizeBoost = isBoosted ? 2.2 : 1.0;
          return basePixelSize * (1 + PULSE_AMPLITUDE * pulse) * sizeBoost;
        }, false) as unknown as Cesium.Property,
        color: new Cesium.CallbackProperty(() => {
          const t = ((Date.now() / PULSE_CYCLE_MS) + phaseOffset) % 1.0;
          const pulse = Math.sin(t * Math.PI * 2) * 0.5 + 0.5;
          const isBoosted = currentVisualMode === 'flir' || currentVisualMode === 'nvg';

          if (isBoosted) {
            // Near-white hot glow — renders as intense hotspot through FLIR/NVG shaders
            hotColor.alpha = 0.95 + 0.05 * pulse;
            return hotColor;
          }

          scratchColor.alpha = BASE_ALPHA + ALPHA_PULSE * pulse;
          return scratchColor;
        }, false) as unknown as Cesium.Property,
        outlineColor: new Cesium.CallbackProperty(() => {
          const isBoosted = currentVisualMode === 'flir' || currentVisualMode === 'nvg';
          return isBoosted
            ? new Cesium.Color(1.0, 1.0, 0.8, 0.7)
            : baseColor.withAlpha(0.3);
        }, false) as unknown as Cesium.Property,
        outlineWidth: new Cesium.CallbackProperty(() => {
          const isBoosted = currentVisualMode === 'flir' || currentVisualMode === 'nvg';
          return isBoosted ? 4 : 2;
        }, false) as unknown as Cesium.Property,
        heightReference: new Cesium.ConstantProperty(
          Cesium.HeightReference.CLAMP_TO_GROUND,
        ),
        scaleByDistance: new Cesium.NearFarScalar(1e4, 1.8, 8e6, 0.2),
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, 20_000_000),
      },
      properties: new Cesium.PropertyBag({
        entityId: fire.id,
        frp,
        confidence,
        brightness: props.brightness,
        satellite: props.satellite,
        instrument: props.instrument,
        daynight: props.daynight,
        acqDate: props.acqDate,
        acqTime: props.acqTime,
      }),
    });
  }

  console.log(
    `[FireLayer] Built ${sorted.length} fire entities ` +
    `(${dataSource.entities.values.length} total)`,
  );
}

// ─── Animation ───────────────────────────────────────────────────

function animLoop(): void {
  if (!state || !animRunning) return;
  state.viewer.scene.requestRender();
  animFrameId = requestAnimationFrame(animLoop);
}

function startAnimation(): void {
  if (animRunning) return;
  animRunning = true;
  animFrameId = requestAnimationFrame(animLoop);
  console.log('[FireLayer] Animation started');
}

function stopAnimation(): void {
  animRunning = false;
  if (animFrameId != null) {
    cancelAnimationFrame(animFrameId);
    animFrameId = null;
  }
}

// ─── Data Loading ────────────────────────────────────────────────

async function loadData(): Promise<void> {
  if (!state || state.loading) return;

  state.loading = true;
  console.log('[FireLayer] Loading FIRMS data...');

  try {
    const entities = await fetchActiveFires();

    if (entities.length === 0) {
      console.warn('[FireLayer] No fire data received');
      return;
    }

    useEntityStore.getState().clearByType('wildfire');
    useEntityStore.getState().upsertEntities(entities);

    buildEntities(entities);
    startAnimation();

    state.loaded = true;
    console.log(`[FireLayer] Loaded ${entities.length} wildfires`);
  } catch (err) {
    console.error('[FireLayer] Failed to load data:', err);
  } finally {
    if (state) state.loading = false;
  }
}

// ─── Public API ──────────────────────────────────────────────────

export function initFireLayer(viewer: Cesium.Viewer): void {
  if (state) return;

  const dataSource = new Cesium.CustomDataSource('wildfires');
  viewer.dataSources.add(dataSource);

  state = {
    viewer,
    dataSource,
    unsubscribeVisibility: null,
    unsubscribeVisualMode: null,
    refreshTimer: null,
    loading: false,
    loaded: false,
  };

  const visible = useGlobeStore.getState().fireLayerVisible;
  dataSource.show = visible;

  state.unsubscribeVisibility = useGlobeStore.subscribe((curr, prev) => {
    if (!state) return;

    if (curr.fireLayerVisible !== prev.fireLayerVisible) {
      state.dataSource.show = curr.fireLayerVisible;

      if (curr.fireLayerVisible) {
        if (!state.loaded) {
          loadData();
        } else {
          startAnimation();
        }
        if (!state.refreshTimer) {
          state.refreshTimer = setInterval(() => loadData(), REFRESH_INTERVAL_MS);
        }
      } else {
        stopAnimation();
        if (state.refreshTimer) {
          clearInterval(state.refreshTimer);
          state.refreshTimer = null;
        }
      }

      viewer.scene.requestRender();
    }
  });

  // ── Visual mode subscription → update intensity for FLIR/NVG ──
  currentVisualMode = useGlobeStore.getState().visualMode;
  state.unsubscribeVisualMode = useGlobeStore.subscribe((curr, prev) => {
    if (curr.visualMode !== prev.visualMode) {
      currentVisualMode = curr.visualMode;
      viewer.scene.requestRender();
      console.log(`[FireLayer] Visual mode → ${curr.visualMode}`);
    }
  });

  if (visible) {
    loadData();
    state.refreshTimer = setInterval(() => loadData(), REFRESH_INTERVAL_MS);
  }

  console.log('[FireLayer] Initialized');
}

export function destroyFireLayer(): void {
  if (!state) return;

  if (state.unsubscribeVisibility) state.unsubscribeVisibility();
  if (state.unsubscribeVisualMode) state.unsubscribeVisualMode();
  if (state.refreshTimer) clearInterval(state.refreshTimer);
  stopAnimation();

  state.viewer.dataSources.remove(state.dataSource, true);
  useEntityStore.getState().clearByType('wildfire');

  state = null;
  console.log('[FireLayer] Destroyed');
}

export function getWildfireCount(): number {
  return selectWildfireCount(useEntityStore.getState());
}
